/**
 * AP Invoices Demo — seed dataset.
 *
 * THROWAWAY DEMO CODE. One coherent story (an industrial MRO / fittings
 * distributor — the same universe as the Demand Radar procurement demo and the
 * AR demo):
 *
 *   • Invoice AIG-90412 is billed by "Apex Industrial Group" — the PARENT
 *     billing entity of our vendor on file, "MRO Supply Co.". The vendor name
 *     isn't on the invoice, yet we matched it because a teammate taught the
 *     system that mapping earlier → a memory-bank note (Beat 2).
 *   • A line reads "Unistrut Channel" but the PO line is "Strut Channel" — a web
 *     agent confirmed "Unistrut" is a brand name used generically for strut
 *     channel, so it matched (Beat 3).
 *   • The proof-of-delivery arrived in a SEPARATE carrier email, was extracted,
 *     and auto-linked to every line (Beat 4) — it shows in the per-line
 *     "Document" column and the full-screen 3-way match.
 *   • Terms are 2/10 Net 30, so payment is scheduled early to capture 2% off,
 *     pending approval (Beat 5).
 *
 * Two more invoices (Border States, Brazos Valley Logistics) carry their own
 * line items + matches so the list/detail views aren't a one-trick pony.
 *
 * NOTE: internal variable names and `AP_DEMO_IDS` keys are intentionally left
 * with their original labels (coastal/freshLink/valleyCold/coldChain) — they're
 * opaque, file-local, and never shown to the user, so renaming them would add
 * churn without changing the demo.
 *
 * All dates are relative to "today" so the data never goes stale.
 */

import {
  SystemEntity,
  EntityAssociationType,
  MatchConfidenceStatus,
  APInvoiceStatus,
} from '@erp/shared-types';
import type {
  APInvoice,
  APInvoiceLineItem,
  APInvoiceRelatedConversation,
  Charge,
  EmailThreadEmail,
  EmailThreadResponse,
  EntityAssociationItem,
  GetAPInvoiceResponse,
  GetPurchaseOrderLineItemResponse,
  GetPurchaseOrderResponse,
  GetShipmentResponse,
  GetSupplierResponse,
  InboundEmail,
  InvoiceChangeLogEntry,
  Payment,
  PurchaseOrderLineItem,
  SupportingDocItem,
} from '@erp/rpc';
import type {
  ApDemoFixtureState,
  ApLogisticsLink,
  ExtractedDocument,
  ThreeWayMatch,
} from './fixture-store.types';

// --- Static asset paths (generated under public/demo/ap/). -----------------
// The document stand-ins are real PDFs (so the in-app viewer renders them as
// PDFs, like a scanned invoice/POD would), generated from the matching `*.svg`
// sources via `rsvg-convert -f pdf -o x.pdf x.svg`. Edit the SVG and re-run to
// regenerate. (`remittance-advice.svg` is unused — RemittanceDialog draws its
// own page — and is left as-is.)
export const BRAND = {
  invoiceImageUrl: '/demo/ap/invoice-mro-supply.pdf',
  podImageUrl: '/demo/ap/pod-sentinel.pdf',
  freightInvoiceImageUrl: '/demo/ap/freight-invoice-sentinel.pdf',
  remittanceImageUrl: '/demo/ap/remittance-advice.svg',
};

// --- Stable IDs (valid UUID v4 shape). -------------------------------------
function pad12(s: string): string {
  return s.padStart(12, '0');
}
export const AP_DEMO_IDS = {
  org: '11111111-1111-4111-8111-111111111111',
  supplierCoastal: '22222222-0000-4000-8000-000000000001',
  supplierFreshLink: '22222222-0000-4000-8000-000000000002',
  supplierValleyCold: '22222222-0000-4000-8000-000000000003',
  supplierColdChain: '22222222-0000-4000-8000-000000000004',
  invoiceAtl: '33333333-0000-4000-8000-000000000001',
  invoiceFreshLink: '33333333-0000-4000-8000-000000000002',
  invoiceValleyCold: '33333333-0000-4000-8000-000000000003',
  invoiceFreight: '33333333-0000-4000-8000-000000000004',
  // Line-item ids namespaced per invoice (inv 1/2/3/4) so they never collide.
  line: (inv: number, n: number) =>
    `44444444-0000-4000-8000-${pad12(`${inv}0${n}`)}`,
  poLine: (po: number, n: number) =>
    `66666666-0000-4000-8000-${pad12(`${po}0${n}`)}`,
  po: '55555555-0000-4000-8000-000000000001',
  poFreshLink: '55555555-0000-4000-8000-000000000002',
  poValleyCold: '55555555-0000-4000-8000-000000000003',
  podDocument: '77777777-0000-4000-8000-000000000001',
  sourceEmail: '88888888-0000-4000-8000-000000000001',
  carrierEmail: '88888888-0000-4000-8000-000000000002',
  // Email-thread conversation ids — the invoice arrived on one thread and the
  // POD + freight invoice on a separate carrier thread.
  conversationSource: '8c000000-0000-4000-8000-000000000001',
  conversationCarrier: '8c000000-0000-4000-8000-000000000002',
  glAccount: '99999999-0000-4000-8000-000000000001',
  payment: (n: number) => `aa000000-0000-4000-8000-${pad12(String(n))}`,
  shipment: 'bb000000-0000-4000-8000-000000000001',
  freightCharge: 'cc000000-0000-4000-8000-000000000001',
  freightChargeCategory: 'dd000000-0000-4000-8000-000000000001',
} as const;

// --- Date helpers. ----------------------------------------------------------
function daysFromNow(days: number): Date {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d;
}
function dateOnly(d: Date): string {
  return d.toISOString().slice(0, 10);
}
// `scheduledPaymentDate` is a real `DataTypes.DATE` timestamp (unlike the
// DATEONLY `preferredPayDate`/`dueDate`/`invoiceDate`). The detail panel parses
// it with `new Date(...)`, so seeding a bare `YYYY-MM-DD` would be read as UTC
// midnight and render a day early west of UTC — leaving it off-by-one from the
// (correctly date-only) preferred pay date. Seed a full timestamp anchored at
// the same local noon `daysFromNow` uses so it renders the right calendar day.
function scheduledTimestamp(d: Date): string {
  return d.toISOString();
}
function plusMinutes(d: Date, minutes: number): Date {
  const next = new Date(d);
  next.setMinutes(next.getMinutes() + minutes);
  return next;
}
function money(value: string | number): string {
  return Number(value).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

interface SeedLineDef {
  n: number;
  description: string;
  qty: number;
  unit: string;
  total: string;
}

/**
 * Build the ingestion change-log for an invoice as a properly-ordered timeline:
 * invoice created → each line created → each line matched → POD linked →
 * workflow completed. Each event gets its own timestamp so the newest-first
 * timeline reads as a real sequence instead of a wall of identical times.
 */
function buildIngestChangeLog(opts: {
  invoiceId: string;
  invIndex: number;
  base: Date;
  supplierId: string;
  lines: SeedLineDef[];
  lineId: (n: number) => string;
  matches: {
    lineNumber: number;
    poLineId: string;
    note?: string;
    targetType?: string;
  }[];
  pod?: { documentId: string; note: string };
}): InvoiceChangeLogEntry[] {
  const { invoiceId, invIndex, base, supplierId, lines, lineId, matches, pod } =
    opts;
  const entries: InvoiceChangeLogEntry[] = [];
  const eid = (suffix: string) =>
    `3c0000${invIndex}-0000-4000-8000-${pad12(suffix)}`;
  let t = 0;

  // 1. Invoice created.
  entries.push({
    id: eid('1'),
    kind: 'field_change',
    entityType: 'ap_invoices',
    entityId: invoiceId,
    action: 'INSERT',
    changedFields: { source: 'email', supplierId },
    actor: {
      userId: null,
      userName: null,
      sourceType: 'system',
      sourceDetail: 'ingested from email',
    },
    createdAt: plusMinutes(base, (t += 1)),
  });

  // 2. One "created" event per line item.
  lines.forEach((l) => {
    entries.push({
      id: eid(`2${l.n}`),
      kind: 'field_change',
      entityType: 'ap_invoice_line_items',
      entityId: lineId(l.n),
      action: 'INSERT',
      changedFields: {
        description: l.description,
        quantity: l.qty,
        unitPrice: l.unit,
      },
      actor: {
        userId: null,
        userName: null,
        sourceType: 'workflow',
        sourceDetail: 'step:create_invoice_draft',
      },
      createdAt: plusMinutes(base, (t += 1)),
    });
  });

  // 3. One "matched" event per matched line item.
  matches.forEach((m) => {
    entries.push({
      id: eid(`3${m.lineNumber}`),
      kind: 'match',
      entityType: 'core_entity_associations',
      entityId: lineId(m.lineNumber),
      action: 'INSERT',
      changedFields: {},
      actor: {
        userId: null,
        userName: null,
        sourceType: 'workflow',
        sourceDetail: m.note ?? 'step:invoice_matching',
      },
      createdAt: plusMinutes(base, (t += 1)),
      matchSource: 'auto',
      matchTargetType: m.targetType ?? 'INTERNAL_PURCHASE_ORDER_LINE_ITEM',
      matchTargetId: m.poLineId,
    });
  });

  // 4. POD linked from the separate carrier email.
  if (pod) {
    entries.push({
      id: eid('4'),
      kind: 'match',
      entityType: 'core_entity_associations',
      entityId: invoiceId,
      action: 'INSERT',
      changedFields: {},
      actor: {
        userId: null,
        userName: null,
        sourceType: 'workflow',
        sourceDetail: pod.note,
      },
      createdAt: plusMinutes(base, (t += 1)),
      matchSource: 'auto',
      matchTargetType: 'CORE_DOCUMENT',
      matchTargetId: pod.documentId,
    });
  }

  // 5. Workflow run completed (anchors the top of the timeline).
  entries.push({
    id: eid('5'),
    kind: 'workflow_run',
    entityType: 'workflow_run',
    entityId: eid('9'),
    action: 'INSERT',
    changedFields: {},
    actor: {
      userId: null,
      userName: null,
      sourceType: 'workflow',
      sourceDetail: 'step:invoice_matching',
    },
    createdAt: plusMinutes(base, (t += 1)),
    workflowRunId: eid('9'),
    workflowId: eid('8'),
    workflowName: 'AP Invoice Ingestion & Matching',
    runStatus: 'completed',
  });

  return entries;
}

function buildLineItems(
  lines: SeedLineDef[],
  lineId: (n: number) => string,
): APInvoiceLineItem[] {
  return lines.map((l) => ({
    id: lineId(l.n),
    lineNumber: l.n,
    description: l.description,
    quantity: l.qty,
    unitPrice: l.unit,
    totalPrice: l.total,
  }));
}

function buildPoLineItems(
  lines: SeedLineDef[],
  names: string[],
  poLineId: (n: number) => string,
): PurchaseOrderLineItem[] {
  return lines.map((l, i) => ({
    id: poLineId(l.n),
    lineNumber: l.n,
    productName: names[i],
    productSku: `SKU-${1000 + l.n}`,
    description: names[i],
    quantity: l.qty,
    unitPrice: l.unit,
    totalPrice: l.total,
    uom: 'EA',
    paymentHold: false,
    troubleData: null,
  }));
}

/**
 * Project an `InboundEmail` into the `EmailThreadEmail` shape the production
 * email-thread viewer renders. Attachments carry over verbatim (already PDFs),
 * so the multi-thread panel renders them inline as PDFs.
 */
function toThreadEmail(
  email: InboundEmail,
  conversationId: string,
): EmailThreadEmail {
  const iso = new Date(email.createdAt).toISOString();
  return {
    id: email.id,
    messageId: email.id,
    conversationId,
    from: email.from,
    to: email.to,
    cc: email.cc ?? null,
    subject: email.subject ?? null,
    textContent: email.textContent ?? null,
    htmlContent: email.htmlContent ?? null,
    attachmentCount: email.attachments?.length ?? 0,
    service: 'email',
    createdAt: iso,
    updatedAt: iso,
    classification: null,
    attachments: (email.attachments ?? []).map((a) => ({
      id: a.id,
      fileName: a.fileName,
      sizeBytes: a.sizeBytes ?? 0,
      mimeType: a.mimeType ?? undefined,
      s3Url: a.s3Url,
    })),
    documentAssociations: [],
    splitDocumentCount: 0,
    splitDocumentAssociations: [],
  };
}

/** Wrap a single inbound email as a one-message thread for `getThread`. */
function toEmailThread(
  email: InboundEmail,
  conversationId: string,
): EmailThreadResponse {
  const iso = new Date(email.createdAt).toISOString();
  return {
    conversationId,
    emails: [toThreadEmail(email, conversationId)],
    totalCount: 1,
    participantEmails: [email.from, email.to].filter(
      (v): v is string => typeof v === 'string',
    ),
    earliestDate: iso,
    latestDate: iso,
  };
}

export function buildApDemoSeed(): ApDemoFixtureState {
  const invoiceDate = daysFromNow(-4);
  const dueDate = daysFromNow(26); // invoiceDate + 30
  const earlyPayDate = daysFromNow(6); // invoiceDate + 10 → 2/10 discount window
  // The original (conservative) schedule: pay one day before the Net 30 due
  // date. The early-pay recommendation proposes moving this up to earlyPayDate.
  const originalScheduledDate = daysFromNow(25); // dueDate − 1
  const createdAt = daysFromNow(-4);

  // --- Supplier (resolved vendor on file). ---------------------------------
  const coastal: GetSupplierResponse = {
    id: AP_DEMO_IDS.supplierCoastal,
    name: 'MRO Supply Co.',
    contactName: 'Marie Hollis',
    email: 'ar@apexindustrial.com',
    phone: '+1 (713) 555-0148',
    address: {
      city: 'Houston',
      state: 'TX',
      street: '2200 Gulf Freeway',
      country: 'US',
      postalCode: '77023',
    },
    paymentTerms: '2/10 Net 30',
    preferredPaymentTerms: '2/10 Net 30',
    preferredOrderMethod: 'email',
    taxId: '56-1839204',
    status: 'active',
    metadata: null,
    companyId: null,
    aliases: [
      {
        id: '2a000000-0000-4000-8000-000000000001',
        alias: 'Apex Industrial Group',
        addedByUserId: null,
        createdAt: daysFromNow(-112),
      },
    ],
  };
  const freshLink: GetSupplierResponse = {
    ...coastal,
    id: AP_DEMO_IDS.supplierFreshLink,
    name: 'Border States',
    email: 'billing@borderstates.com',
    // The supply agreement (SA-BRST-2024-011 §4.2) is Net 45.
    paymentTerms: 'Net 45',
    preferredPaymentTerms: 'Net 45',
    aliases: [],
  };
  const valleyCold: GetSupplierResponse = {
    ...coastal,
    id: AP_DEMO_IDS.supplierValleyCold,
    name: 'Brazos Valley Logistics',
    email: 'accounts@brazosvalleylogistics.com',
    paymentTerms: 'Net 45',
    preferredPaymentTerms: 'Net 45',
    aliases: [],
  };

  const supplierSummary = {
    id: coastal.id,
    name: coastal.name,
    contactName: coastal.contactName,
    email: coastal.email,
    phone: coastal.phone,
    paymentTerms: coastal.paymentTerms,
    status: coastal.status,
    notes: null,
    defaultPaymentMethod: 'ach',
    glAccounts: null,
  };

  // ===================================================================
  // HERO INVOICE — AIG-90412
  // ===================================================================
  const heroLines: SeedLineDef[] = [
    {
      n: 1,
      description: 'Unistrut Channel, 1-5/8" x 10 ft',
      qty: 120,
      unit: '30.00',
      total: '3600.00',
    },
    {
      n: 2,
      description: 'Hydraulic Quick Coupler — 1/2" NPT, Flat-Face',
      qty: 100,
      unit: '24.00',
      total: '2400.00',
    },
    {
      n: 3,
      description: 'Galvanized Mounting Bracket — Heavy Duty',
      qty: 80,
      unit: '22.50',
      total: '1800.00',
    },
    {
      n: 4,
      description: 'Brass Ball Valve — 1" NPT',
      qty: 80,
      unit: '25.00',
      total: '2000.00',
    },
  ];
  const heroPoNames = [
    'Strut Channel (12 ga), 1-5/8" x 10 ft',
    'Hydraulic Quick Coupler — 1/2" NPT, Flat-Face',
    'Galvanized Mounting Bracket — Heavy Duty',
    'Brass Ball Valve — 1" NPT',
  ];
  const heroLineId = (n: number) => AP_DEMO_IDS.line(1, n);
  const heroPoLineId = (n: number) => AP_DEMO_IDS.poLine(1, n);

  const lineItems = buildLineItems(heroLines, heroLineId);
  const poLineItemsArr = buildPoLineItems(heroLines, heroPoNames, heroPoLineId);

  const purchaseOrder: GetPurchaseOrderResponse['purchaseOrder'] = {
    id: AP_DEMO_IDS.po,
    organizationId: AP_DEMO_IDS.org,
    internalId: 5582,
    externalId: 'PO-5582',
    supplierId: coastal.id,
    orderDate: dateOnly(daysFromNow(-18)),
    deliveryDate: dateOnly(daysFromNow(-6)),
    status: 'FULLY_RECEIVED',
    totalAmount: '9800.00',
    currency: 'USD',
    receivedFlag: true,
    notes: null,
    createdAt: daysFromNow(-18),
    updatedAt: daysFromNow(-6),
  };
  const poResponse: GetPurchaseOrderResponse = {
    purchaseOrder,
    lineItems: poLineItemsArr,
  };

  // --- Source invoice email (rendered by the existing email viewer). -------
  const sourceEmail: InboundEmail = {
    id: AP_DEMO_IDS.sourceEmail,
    conversationId: AP_DEMO_IDS.conversationSource,
    subject: 'Invoice AIG-90412 for order PO-5582',
    from: 'Accounts Receivable <ar@apexindustrial.com>',
    to: 'payables@gently-demo.com',
    cc: null,
    htmlContent: `
      <div style="font-family: Arial, sans-serif; color: #1f2937; font-size: 14px; line-height: 1.6;">
        <p>Hi Payables team,</p>
        <p>Please find attached <strong>Invoice AIG-90412</strong> for your order
        <strong>PO-5582</strong>, delivered last week. Terms are <strong>2/10 Net 30</strong> —
        a 2% discount applies if paid within 10 days.</p>
        <p>Thanks,<br/>Marie Hollis<br/>Apex Industrial Group</p>
      </div>`,
    textContent:
      'Please find attached Invoice AIG-90412 for PO-5582. Terms 2/10 Net 30. Billed by Apex Industrial Group.',
    attachments: [
      {
        id: '8a000000-0000-4000-8000-000000000001',
        fileName: 'Invoice-AIG-90412.pdf',
        sizeBytes: 80689,
        mimeType: 'application/pdf',
        s3Url: BRAND.invoiceImageUrl,
        contentId: null,
      },
    ],
    createdAt,
  };

  const atlInvoice: APInvoice = {
    id: AP_DEMO_IDS.invoiceAtl,
    organizationId: AP_DEMO_IDS.org,
    supplierId: coastal.id,
    invoiceNumber: 'AIG-90412',
    referenceIds: ['PO-5582'],
    invoiceDate: dateOnly(invoiceDate),
    receivedAt: invoiceDate.toISOString(),
    dueDate: dateOnly(dueDate),
    subtotal: '9800.00',
    taxAmount: '0.00',
    totalAmount: '9800.00',
    currency: 'USD',
    paymentTerms: '2/10 Net 30',
    paymentStatus: 'unpaid',
    // Preferred pay date = the early-discount date the agent recommends;
    // scheduled pay date = the original schedule (one day before due).
    preferredPayDate: dateOnly(earlyPayDate),
    scheduledPaymentDate: scheduledTimestamp(originalScheduledDate),
    scheduledPaymentExplanation: `Scheduled for ${dateOnly(
      originalScheduledDate,
    )}, one day before the Net 30 due date. Terms are 2/10 Net 30 — paying by ${dateOnly(
      earlyPayDate,
    )} instead would capture a 2% discount ($9,604.00 vs $9,800.00, saving $196.00). See the early-payment recommendation below.`,
    isVoided: false,
    paymentHold: false,
    status: APInvoiceStatus.MATCHED,
    source: 'email',
    sourceId: AP_DEMO_IDS.sourceEmail,
    glAccountId: AP_DEMO_IDS.glAccount,
    notes: null,
    internalNotes: null,
    supplier: supplierSummary,
    createdAt,
    updatedAt: createdAt,
    lineItemMatchSummary: {
      matched: 4,
      confirmed: 3,
      tentative: 1,
      unmatched: 0,
      total: 4,
    },
  };

  // --- Supporting doc: the POD (arrived in the carrier email). -------------
  const podDoc: SupportingDocItem = {
    documentId: AP_DEMO_IDS.podDocument,
    classificationType: 'proof_of_delivery',
    originalFileName: 'POD-PO-5582-sentinel.pdf',
    mimeType: 'application/pdf',
    fileSizeBytes: 96344,
    classifiedAt: daysFromNow(-3),
    classificationConfidence: 0.97,
    associatedAt: daysFromNow(-3),
    ocr: {
      id: '7a000000-0000-4000-8000-000000000001',
      status: 'completed',
      vendor: 'extend',
      modelVersion: 'extract-1.4',
      error: null,
      createdAt: daysFromNow(-3),
    },
    summary: {
      classificationType: 'proof_of_delivery',
      bolNumber: 'BOL-44817',
      proNumber: 'PRO-90126633',
      carrierName: 'Sentinel Freight',
      deliveryDate: dateOnly(daysFromNow(-6)),
      receivedByName: 'M. Alvarez (Receiving)',
      signaturePresent: true,
      poNumbers: ['PO-5582'],
      soNumbers: [],
      lineItemCount: 4,
      exceptionCount: 0,
    },
  };

  // --- Extracted POD document (structured fields + line items). ------------
  const podExtracted: ExtractedDocument = {
    kind: 'proof_of_delivery',
    title: 'Proof of Delivery · PRO-90126633',
    issuer: 'Sentinel Freight',
    sourceNote:
      'Extracted from a separate carrier email thread (Sentinel Freight), not the invoice email.',
    matchReason:
      'The POD cites BOL-44817 → PO-5582, and all 4 delivered items + unit counts reconcile with PO-5582 (380 units, no exceptions), so it was auto-linked to this invoice.',
    fields: [
      { label: 'BOL #', value: 'BOL-44817' },
      { label: 'PO #', value: 'PO-5582', emphasize: true },
      { label: 'Carrier', value: 'Sentinel Freight' },
      { label: 'Delivery date', value: dateOnly(daysFromNow(-6)) },
      { label: 'Received by', value: 'M. Alvarez (Receiving)' },
      { label: 'Signature on file', value: 'Yes' },
      { label: 'Exceptions', value: 'None' },
    ],
    columns: { quantity: 'Shipped', amount: 'Received' },
    // Rendered in invoice order; the # is the POD's own (different) scan order.
    lineItems: [
      {
        number: 2, // Strut Channel — 2nd line scanned on the POD
        description: 'Strut Channel (12 ga), 1-5/8" x 10 ft',
        note: 'Billed as “Unistrut Channel” on the invoice',
        quantity: '120',
        amount: '120 ✓',
        status: 'match',
      },
      {
        number: 4, // Hydraulic Quick Coupler — 4th
        description: 'Hydraulic Quick Coupler — 1/2" NPT, Flat-Face',
        quantity: '100',
        amount: '100 ✓',
        status: 'match',
      },
      {
        number: 1, // Galvanized Mounting Bracket — 1st
        description: 'Galvanized Mounting Bracket — Heavy Duty',
        quantity: '80',
        amount: '80 ✓',
        status: 'match',
      },
      {
        number: 3, // Brass Ball Valve — 3rd
        description: 'Brass Ball Valve — 1" NPT',
        quantity: '80',
        amount: '80 ✓',
        status: 'match',
      },
    ],
  };

  // --- Extracted invoice + PO documents for the 3-way overlay. -------------
  const invoiceExtracted: ExtractedDocument = {
    kind: 'invoice',
    title: 'Invoice AIG-90412',
    issuer: 'Apex Industrial Group (for MRO Supply Co.)',
    fields: [
      { label: 'PO #', value: 'PO-5582', emphasize: true },
      { label: 'Invoice date', value: dateOnly(invoiceDate) },
      { label: 'Terms', value: '2/10 Net 30' },
      { label: 'Subtotal', value: money('9800.00') },
      { label: 'Tax', value: money('0.00') },
      { label: 'Total', value: money('9800.00') },
    ],
    columns: { quantity: 'Qty', secondary: 'Unit', amount: 'Amount' },
    // Invoice defines the order: lines 1–4 in the order they appear on the bill.
    lineItems: heroLines.map((l) => ({
      number: l.n,
      description: l.description,
      note: l.n === 1 ? 'Matched to PO “Strut Channel” via web agent' : null,
      quantity: String(l.qty),
      secondary: money(l.unit),
      amount: money(l.total),
      status: l.n === 1 ? 'info' : 'match',
    })),
  };
  // The PO and POD were entered/scanned in DIFFERENT line orders than the
  // invoice. In the 3-way view their rows are reordered to align with the
  // invoice (keyed by invoice line number) but keep their own line numbers — so
  // the # column reads non-sequential. Maps: invoice line # → source line #.
  const poLineNumberByInvoiceLine: Record<number, number> = {
    1: 3, // Strut Channel — 3rd line on the PO
    2: 1, // Hydraulic Quick Coupler — 1st on the PO
    3: 4, // Galvanized Mounting Bracket — 4th on the PO
    4: 2, // Brass Ball Valve — 2nd on the PO
  };
  const poExtracted: ExtractedDocument = {
    kind: 'purchase_order',
    title: 'PO-5582',
    issuer: 'Gently — Procurement',
    fields: [
      { label: 'PO #', value: 'PO-5582', emphasize: true },
      { label: 'Status', value: 'Fully received' },
      { label: 'Order date', value: dateOnly(daysFromNow(-18)) },
      { label: 'Delivery date', value: dateOnly(daysFromNow(-6)) },
      { label: 'Supplier', value: 'MRO Supply Co.' },
      { label: 'Total', value: money('9800.00') },
    ],
    columns: { quantity: 'Qty', secondary: 'Unit', amount: 'Amount' },
    // Rendered in invoice order, but each row keeps its own PO line number.
    lineItems: heroLines.map((l, i) => ({
      number: poLineNumberByInvoiceLine[l.n],
      description: heroPoNames[i],
      quantity: String(l.qty),
      secondary: money(l.unit),
      amount: money(l.total),
      status: 'match',
    })),
  };

  // --- Associations: invoice → PO, each line → PO line + POD document. ------
  const associations: Record<string, EntityAssociationItem[]> = {
    [AP_DEMO_IDS.invoiceAtl]: [
      {
        entity: {
          entityId: AP_DEMO_IDS.po,
          entityType: SystemEntity.INTERNAL_PURCHASE_ORDER,
        },
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
        metadata: null,
      },
    ],
  };
  heroLines.forEach((l) => {
    // Line 1 (Unistrut → Strut Channel) is the web-agent match — tentative.
    const isWebMatch = l.n === 1;
    associations[heroLineId(l.n)] = [
      {
        entity: {
          entityId: heroPoLineId(l.n),
          entityType: SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM,
        },
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
        metadata: {
          confidence: isWebMatch ? 0.78 : 0.99,
          matchStatus: isWebMatch
            ? MatchConfidenceStatus.TENTATIVE
            : MatchConfidenceStatus.CONFIRMED,
        },
      },
      // 3rd-way match: the proof-of-delivery substantiates every line.
      {
        entity: {
          entityId: AP_DEMO_IDS.podDocument,
          entityType: SystemEntity.CORE_DOCUMENT,
        },
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
        metadata: {
          confidence: 0.97,
          matchStatus: MatchConfidenceStatus.CONFIRMED,
        },
      },
    ];
  });

  const heroChangeLog = buildIngestChangeLog({
    invoiceId: AP_DEMO_IDS.invoiceAtl,
    invIndex: 1,
    base: createdAt,
    supplierId: coastal.id,
    lines: heroLines,
    lineId: heroLineId,
    matches: [
      // The Unistrut→Strut-Channel web-agent provenance shows on the line's
      // MatchProvenanceChip; keep the changelog actor uniform across lines.
      { lineNumber: 1, poLineId: heroPoLineId(1) },
      { lineNumber: 2, poLineId: heroPoLineId(2) },
      { lineNumber: 3, poLineId: heroPoLineId(3) },
      { lineNumber: 4, poLineId: heroPoLineId(4) },
    ],
    pod: {
      documentId: AP_DEMO_IDS.podDocument,
      note: 'linked POD from a separate carrier email (Sentinel Freight)',
    },
  });

  // Two email threads contributed to this invoice — the original invoice email
  // and a separate carrier thread that delivered the POD + freight invoice. The
  // production email panel stacks them ("Original Thread" / "Update Thread").
  const heroRelatedConversations: APInvoiceRelatedConversation[] = [
    {
      conversationId: AP_DEMO_IDS.conversationSource,
      primaryEmailId: AP_DEMO_IDS.sourceEmail,
      subject: sourceEmail.subject ?? null,
      isSource: true,
    },
    {
      conversationId: AP_DEMO_IDS.conversationCarrier,
      primaryEmailId: AP_DEMO_IDS.carrierEmail,
      subject: 'POD + freight invoice for PO-5582 delivery',
      isSource: false,
    },
  ];

  const atlDetail: GetAPInvoiceResponse = {
    invoice: atlInvoice,
    lineItems,
    inboundEmail: sourceEmail,
    relatedConversations: heroRelatedConversations,
  };

  const threeWay: ThreeWayMatch = {
    invoiceNumber: 'AIG-90412',
    poNumber: 'PO-5582',
    status: 'matched',
    summary:
      'All 4 line items reconcile across the invoice, PO and proof of delivery — quantities and amounts agree, and delivery is confirmed with no exceptions.',
    podSourceNote:
      'Proof of delivery sourced from a separate carrier email (Sentinel Freight) and linked automatically.',
    rows: [
      {
        field: 'PO number',
        invoiceValue: 'PO-5582',
        poValue: 'PO-5582',
        podValue: 'PO-5582',
        match: true,
      },
      {
        field: 'Total units',
        invoiceValue: '380',
        poValue: '380',
        podValue: '380',
        match: true,
      },
      {
        field: 'Total amount',
        invoiceValue: money('9800.00'),
        poValue: money('9800.00'),
        podValue: '—',
        match: true,
      },
      {
        field: 'Delivery date',
        invoiceValue: '—',
        poValue: dateOnly(daysFromNow(-6)),
        podValue: dateOnly(daysFromNow(-6)),
        match: true,
      },
      {
        field: 'Signed for',
        invoiceValue: '—',
        poValue: '—',
        podValue: 'M. Alvarez (Receiving)',
        match: true,
      },
      {
        field: 'Exceptions',
        invoiceValue: '—',
        poValue: '—',
        podValue: 'None',
        match: true,
      },
    ],
    documents: [invoiceExtracted, poExtracted, podExtracted],
  };

  // ===================================================================
  // BORDER STATES INVOICE — BS-22188 (ACCEPTED, Net 30, fully matched)
  // ===================================================================
  const flLines: SeedLineDef[] = [
    {
      n: 1,
      description: 'EMT Conduit — 3/4" x 10 ft',
      qty: 60,
      unit: '28.00',
      total: '1680.00',
    },
    {
      n: 2,
      description: 'Weatherproof Junction Box — 1-Gang, Cast Aluminum',
      qty: 50,
      unit: '25.00',
      total: '1250.00',
    },
    {
      n: 3,
      description: 'Liquidtight Connector — 3/4" Straight',
      qty: 80,
      unit: '16.50',
      total: '1320.00',
    },
  ];
  const flPoNames = [
    'EMT Conduit — 3/4" x 10 ft',
    'Weatherproof Junction Box — 1-Gang, Cast Aluminum',
    'Liquidtight Connector — 3/4" Straight',
  ];
  // Invoice date (9 days ago) + 45 days.
  const bsNet45PayDate = daysFromNow(36);
  const flLineId = (n: number) => AP_DEMO_IDS.line(2, n);
  const flPoLineId = (n: number) => AP_DEMO_IDS.poLine(2, n);

  const flPurchaseOrder: GetPurchaseOrderResponse['purchaseOrder'] = {
    id: AP_DEMO_IDS.poFreshLink,
    organizationId: AP_DEMO_IDS.org,
    internalId: 5571,
    externalId: 'PO-5571',
    supplierId: freshLink.id,
    orderDate: dateOnly(daysFromNow(-20)),
    deliveryDate: dateOnly(daysFromNow(-11)),
    status: 'FULLY_RECEIVED',
    totalAmount: '4250.00',
    currency: 'USD',
    receivedFlag: true,
    notes: null,
    createdAt: daysFromNow(-20),
    updatedAt: daysFromNow(-11),
  };
  const flPoLineItemsArr = buildPoLineItems(flLines, flPoNames, flPoLineId);
  const flPoResponse: GetPurchaseOrderResponse = {
    purchaseOrder: flPurchaseOrder,
    lineItems: flPoLineItemsArr,
  };

  const freshLinkInvoice: APInvoice = {
    ...atlInvoice,
    id: AP_DEMO_IDS.invoiceFreshLink,
    supplierId: freshLink.id,
    invoiceNumber: 'BS-22188',
    referenceIds: ['PO-5571'],
    invoiceDate: dateOnly(daysFromNow(-9)),
    receivedAt: daysFromNow(-9).toISOString(),
    dueDate: dateOnly(daysFromNow(21)),
    subtotal: '4250.00',
    taxAmount: '0.00',
    totalAmount: '4250.00',
    paymentTerms: 'Net 30',
    // Procurement's invoice compliance corrected the terms: the agreement
    // (SA-BRST-2024-011 §4.2) is Net 45, so payment is scheduled for invoice
    // date + 45 days, not the Net 30 date the invoice prints.
    scheduledPaymentDate: scheduledTimestamp(bsNet45PayDate),
    scheduledPaymentExplanation: `Scheduled for ${dateOnly(
      bsNet45PayDate,
    )} on the agreement's Net 45 terms (SA-BRST-2024-011 §4.2). The invoice states Net 30, due ${dateOnly(
      daysFromNow(21),
    )}, but a shorter term printed on an invoice has no effect.`,
    preferredPayDate: null,
    status: APInvoiceStatus.ACCEPTED,
    source: 'email',
    sourceId: null,
    glAccountId: null,
    supplier: { ...supplierSummary, id: freshLink.id, name: freshLink.name },
    createdAt: daysFromNow(-9),
    updatedAt: daysFromNow(-9),
    lineItemMatchSummary: {
      matched: 3,
      confirmed: 3,
      tentative: 0,
      unmatched: 0,
      total: 3,
    },
  };

  const flAssociations: Record<string, EntityAssociationItem[]> = {
    [AP_DEMO_IDS.invoiceFreshLink]: [
      {
        entity: {
          entityId: AP_DEMO_IDS.poFreshLink,
          entityType: SystemEntity.INTERNAL_PURCHASE_ORDER,
        },
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
        metadata: null,
      },
    ],
  };
  flLines.forEach((l) => {
    flAssociations[flLineId(l.n)] = [
      {
        entity: {
          entityId: flPoLineId(l.n),
          entityType: SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM,
        },
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
        metadata: {
          confidence: 0.99,
          matchStatus: MatchConfidenceStatus.CONFIRMED,
        },
      },
    ];
  });
  const flChangeLog = buildIngestChangeLog({
    invoiceId: AP_DEMO_IDS.invoiceFreshLink,
    invIndex: 2,
    base: daysFromNow(-9),
    supplierId: freshLink.id,
    lines: flLines,
    lineId: flLineId,
    matches: flLines.map((l) => ({
      lineNumber: l.n,
      poLineId: flPoLineId(l.n),
    })),
  });

  // ===================================================================
  // BRAZOS VALLEY LOGISTICS INVOICE — BVL-7741 (DRAFT, Net 45, partial match)
  // ===================================================================
  const vcLines: SeedLineDef[] = [
    {
      n: 1,
      description: 'Inbound Pallet Handling (in/out)',
      qty: 40,
      unit: '18.00',
      total: '720.00',
    },
    {
      n: 2,
      description: 'Outbound Crating & Export Packaging',
      qty: 1,
      unit: '600.00',
      total: '600.00',
    },
  ];
  const vcPoNames = [
    'Pallet Handling (in/out), per pallet',
    'Ambient Storage, per pallet/month',
  ];
  const vcLineId = (n: number) => AP_DEMO_IDS.line(3, n);
  const vcPoLineId = (n: number) => AP_DEMO_IDS.poLine(3, n);

  const vcPurchaseOrder: GetPurchaseOrderResponse['purchaseOrder'] = {
    id: AP_DEMO_IDS.poValleyCold,
    organizationId: AP_DEMO_IDS.org,
    internalId: 7740,
    externalId: 'PO-7740',
    supplierId: valleyCold.id,
    orderDate: dateOnly(daysFromNow(-15)),
    deliveryDate: dateOnly(daysFromNow(-2)),
    status: 'PARTIALLY_RECEIVED',
    totalAmount: '720.00',
    currency: 'USD',
    receivedFlag: false,
    notes: null,
    createdAt: daysFromNow(-15),
    updatedAt: daysFromNow(-2),
  };
  const vcPoLineItemsArr = buildPoLineItems(
    [
      {
        n: 1,
        description: vcPoNames[0],
        qty: 40,
        unit: '18.00',
        total: '720.00',
      },
      {
        n: 2,
        description: vcPoNames[1],
        qty: 10,
        unit: '12.00',
        total: '120.00',
      },
    ],
    vcPoNames,
    vcPoLineId,
  );
  const vcPoResponse: GetPurchaseOrderResponse = {
    purchaseOrder: vcPurchaseOrder,
    lineItems: vcPoLineItemsArr,
  };

  const valleyColdInvoice: APInvoice = {
    ...atlInvoice,
    id: AP_DEMO_IDS.invoiceValleyCold,
    supplierId: valleyCold.id,
    invoiceNumber: 'BVL-7741',
    referenceIds: ['PO-7740'],
    invoiceDate: dateOnly(daysFromNow(-1)),
    receivedAt: daysFromNow(-1).toISOString(),
    dueDate: dateOnly(daysFromNow(44)),
    subtotal: '1320.00',
    taxAmount: '0.00',
    totalAmount: '1320.00',
    paymentTerms: 'Net 45',
    scheduledPaymentDate: null,
    scheduledPaymentExplanation: null,
    preferredPayDate: null,
    status: APInvoiceStatus.DRAFT,
    source: 'email',
    sourceId: null,
    glAccountId: null,
    supplier: { ...supplierSummary, id: valleyCold.id, name: valleyCold.name },
    createdAt: daysFromNow(-1),
    updatedAt: daysFromNow(-1),
    lineItemMatchSummary: {
      matched: 1,
      confirmed: 0,
      tentative: 1,
      unmatched: 1,
      total: 2,
    },
  };

  const vcAssociations: Record<string, EntityAssociationItem[]> = {
    [AP_DEMO_IDS.invoiceValleyCold]: [
      {
        entity: {
          entityId: AP_DEMO_IDS.poValleyCold,
          entityType: SystemEntity.INTERNAL_PURCHASE_ORDER,
        },
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
        metadata: null,
      },
    ],
    // Line 1 → tentative match; line 2 is intentionally left unmatched.
    [vcLineId(1)]: [
      {
        entity: {
          entityId: vcPoLineId(1),
          entityType: SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM,
        },
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
        metadata: {
          confidence: 0.74,
          matchStatus: MatchConfidenceStatus.TENTATIVE,
        },
      },
    ],
  };
  const vcChangeLog = buildIngestChangeLog({
    invoiceId: AP_DEMO_IDS.invoiceValleyCold,
    invIndex: 3,
    base: daysFromNow(-1),
    supplierId: valleyCold.id,
    lines: vcLines,
    lineId: vcLineId,
    matches: [{ lineNumber: 1, poLineId: vcPoLineId(1) }],
  });

  // ===================================================================
  // FREIGHT INVOICE — SF-3398 (Sentinel Freight, the carrier)
  // The POD + this freight invoice arrived in ONE carrier email thread. The
  // freight invoice is matched to the SHIPMENT (SHP-44817) that delivered
  // PO-5582 — its line matching the shipment's freight charge — so the chain is
  // freight invoice → shipment → PO.
  // ===================================================================
  const coldChain: GetSupplierResponse = {
    ...coastal,
    id: AP_DEMO_IDS.supplierColdChain,
    name: 'Sentinel Freight',
    contactName: 'Dispatch',
    email: 'ap@sentinelfreight.com',
    phone: '+1 (469) 555-0193',
    paymentTerms: 'Net 15',
    preferredPaymentTerms: 'Net 15',
    aliases: [],
  };

  const frtLines: SeedLineDef[] = [
    {
      n: 1,
      description: 'LTL freight — PO-5582 delivery',
      qty: 1,
      unit: '1200.00',
      total: '1200.00',
    },
  ];
  const frtLineId = (n: number) => AP_DEMO_IDS.line(4, n);

  // The shipment that delivered PO-5582 — tied to the PO via `purchaseOrders`,
  // and carrying the freight charge the invoice line matches.
  const shipment: GetShipmentResponse = {
    shipment: {
      id: AP_DEMO_IDS.shipment,
      organizationId: AP_DEMO_IDS.org,
      type: 'incoming',
      status: 'delivered',
      origin: 'Houston, TX',
      destination: 'Gently DC — Dallas, TX',
      scheduledAt: daysFromNow(-7).toISOString(),
      departureAt: daysFromNow(-7).toISOString(),
      arrivalAt: daysFromNow(-6).toISOString(),
      weight: 18450,
      volume: null,
      notes: null,
      originLoadId: null,
      originLoadNumber: 'SHP-44817',
      originSystem: null,
      createdAt: daysFromNow(-7),
      updatedAt: daysFromNow(-6),
    },
    salesOrders: [],
    purchaseOrders: [
      {
        id: AP_DEMO_IDS.po,
        externalId: 'PO-5582',
        internalId: 5582,
        supplierName: 'MRO Supply Co.',
        totalAmount: '9800.00',
        status: 'FULLY_RECEIVED',
      },
    ],
    orderedDestinations: [],
    chargeBreakdowns: [
      {
        shipmentChargeId: AP_DEMO_IDS.freightCharge,
        categoryLabel: 'Freight',
        amount: '1200.00',
        direction: 'DEBIT',
        allocations: [],
      },
    ],
    chargesCount: 1,
  };

  const freightCharge: Charge = {
    id: AP_DEMO_IDS.freightCharge,
    chargeCategoryId: AP_DEMO_IDS.freightChargeCategory,
    direction: 'DEBIT',
    isEstimate: false,
    amount: '1200.00',
    currencyCode: 'USD',
    docType: SystemEntity.SCM_SHIPMENT,
    docId: AP_DEMO_IDS.shipment,
    docLineId: null,
    troubleData: null,
    isVisibleOnExternalDocuments: true,
    createdAt: daysFromNow(-6),
    updatedAt: daysFromNow(-6),
    chargeCategory: {
      id: AP_DEMO_IDS.freightChargeCategory,
      value: 'freight',
      label: 'Freight',
    },
  };

  // The shared carrier email thread (POD + freight invoice attachments).
  const carrierEmailMsg: InboundEmail = {
    id: AP_DEMO_IDS.carrierEmail,
    conversationId: AP_DEMO_IDS.conversationCarrier,
    subject: 'POD + freight invoice for PO-5582 delivery',
    from: 'Dispatch <dispatch@sentinelfreight.com>',
    to: 'payables@gently-demo.com',
    cc: null,
    htmlContent: `
      <div style="font-family: Arial, sans-serif; color: #1f2937; font-size: 14px; line-height: 1.6;">
        <p>Hi Payables team,</p>
        <p>We delivered <strong>PO-5582</strong> last week — 380 units, signed for, no
        exceptions. Attached is the signed <strong>proof of delivery</strong> and our
        <strong>freight invoice SF-3398</strong> ($1,200.00, Net 15).</p>
        <p>Thanks,<br/>Dispatch<br/>Sentinel Freight</p>
      </div>`,
    textContent:
      'Delivered PO-5582 (380 units, no exceptions). Attached: signed POD + freight invoice SF-3398 ($1,200.00).',
    attachments: [
      {
        id: '8a000000-0000-4000-8000-000000000010',
        fileName: 'POD-PO-5582-sentinel.pdf',
        sizeBytes: 77113,
        mimeType: 'application/pdf',
        s3Url: BRAND.podImageUrl,
        contentId: null,
      },
      {
        id: '8a000000-0000-4000-8000-000000000011',
        fileName: 'Freight-Invoice-SF-3398.pdf',
        sizeBytes: 62775,
        mimeType: 'application/pdf',
        s3Url: BRAND.freightInvoiceImageUrl,
        contentId: null,
      },
    ],
    createdAt: daysFromNow(-3),
  };

  const freightInvoice: APInvoice = {
    ...atlInvoice,
    id: AP_DEMO_IDS.invoiceFreight,
    supplierId: coldChain.id,
    invoiceNumber: 'SF-3398',
    referenceIds: ['SHP-44817'],
    invoiceDate: dateOnly(daysFromNow(-3)),
    receivedAt: daysFromNow(-3).toISOString(),
    dueDate: dateOnly(daysFromNow(12)),
    subtotal: '1200.00',
    taxAmount: '0.00',
    totalAmount: '1200.00',
    currency: 'USD',
    paymentTerms: 'Net 15',
    scheduledPaymentDate: scheduledTimestamp(daysFromNow(11)), // dueDate − 1
    scheduledPaymentExplanation: `Scheduled for ${dateOnly(
      daysFromNow(11),
    )}, one day before the Net 15 due date (no early-pay discount available).`,
    preferredPayDate: null,
    status: APInvoiceStatus.ACCEPTED,
    source: 'email',
    sourceId: AP_DEMO_IDS.carrierEmail,
    glAccountId: null,
    supplier: { ...supplierSummary, id: coldChain.id, name: coldChain.name },
    createdAt: daysFromNow(-3),
    updatedAt: daysFromNow(-3),
    lineItemMatchSummary: {
      matched: 1,
      confirmed: 1,
      tentative: 0,
      unmatched: 0,
      total: 1,
    },
  };

  const freightDetail: GetAPInvoiceResponse = {
    invoice: freightInvoice,
    lineItems: buildLineItems(frtLines, frtLineId),
    inboundEmail: carrierEmailMsg,
    relatedConversations: [],
  };

  const frtAssociations: Record<string, EntityAssociationItem[]> = {
    // Header match: the freight invoice is associated to the SHIPMENT.
    [AP_DEMO_IDS.invoiceFreight]: [
      {
        entity: {
          entityId: AP_DEMO_IDS.shipment,
          entityType: SystemEntity.SCM_SHIPMENT,
        },
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
        metadata: null,
      },
    ],
    // Line match: the freight line matches the shipment's freight charge.
    [frtLineId(1)]: [
      {
        entity: {
          entityId: AP_DEMO_IDS.freightCharge,
          entityType: SystemEntity.CHARGE,
        },
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
        metadata: {
          confidence: 0.99,
          matchStatus: MatchConfidenceStatus.CONFIRMED,
        },
      },
    ],
  };
  const frtChangeLog = buildIngestChangeLog({
    invoiceId: AP_DEMO_IDS.invoiceFreight,
    invIndex: 4,
    base: daysFromNow(-3),
    supplierId: coldChain.id,
    lines: frtLines,
    lineId: frtLineId,
    matches: [
      {
        lineNumber: 1,
        poLineId: AP_DEMO_IDS.freightCharge,
        targetType: 'CHARGE',
      },
    ],
  });

  // --- Product ↔ freight cross-links (same carrier email thread). ----------
  const logisticsLinkByInvoiceId: Record<string, ApLogisticsLink> = {
    [AP_DEMO_IDS.invoiceAtl]: {
      relatedInvoiceId: AP_DEMO_IDS.invoiceFreight,
      relatedInvoiceNumber: 'SF-3398',
      relatedKind: 'freight',
      carrierName: 'Sentinel Freight',
      carrierEmailSubject: carrierEmailMsg.subject ?? '',
      podMatchReason:
        'The proof of delivery cites BOL-44817 → PO-5582 and reconciles all 4 delivered items (380 units, no exceptions) against the PO, so it was auto-linked to this invoice.',
      chainNote:
        'The carrier billed freight separately on invoice SF-3398, matched to shipment SHP-44817 (the delivery for this PO).',
    },
    [AP_DEMO_IDS.invoiceFreight]: {
      relatedInvoiceId: AP_DEMO_IDS.invoiceAtl,
      relatedInvoiceNumber: 'AIG-90412',
      relatedKind: 'product',
      carrierName: 'Sentinel Freight',
      carrierEmailSubject: carrierEmailMsg.subject ?? '',
      chainNote:
        'Matched to shipment SHP-44817 — the delivery for order PO-5582 (product invoice AIG-90412).',
    },
  };

  // --- Payments shown on the Payments page. --------------------------------
  function buildPayment(args: {
    n: number;
    amount: string;
    status: string;
    method: string;
    paymentDate: Date;
    supplier: GetSupplierResponse;
    invoiceId: string;
    invoiceNumber: string;
    createdAt: Date;
    memo?: string;
  }): Payment {
    return {
      id: AP_DEMO_IDS.payment(args.n),
      organizationId: AP_DEMO_IDS.org,
      amount: args.amount,
      currency: 'USD',
      paymentDate: dateOnly(args.paymentDate),
      method: args.method,
      status: args.status,
      memo: args.memo,
      createdAt: args.createdAt,
      updatedAt: args.createdAt,
      invoices: [
        {
          id: args.invoiceId,
          invoiceNumber: args.invoiceNumber,
          amount: args.amount,
          totalAmount: args.amount,
          currency: 'USD',
          supplierId: args.supplier.id,
          supplier: {
            id: args.supplier.id,
            name: args.supplier.name,
            status: args.supplier.status,
          },
        },
      ],
    };
  }

  const payments: Payment[] = [
    buildPayment({
      n: 1,
      amount: '4250.00',
      status: 'scheduled',
      method: 'ach',
      // Procurement corrected the terms: Net 45 from the invoice date, not the
      // Net 30 the invoice prints (see `pages/compliance` in the procurement demo).
      paymentDate: bsNet45PayDate,
      supplier: freshLink,
      invoiceId: AP_DEMO_IDS.invoiceFreshLink,
      invoiceNumber: 'BS-22188',
      createdAt: daysFromNow(-9),
      memo: 'Net 45 per SA-BRST-2024-011 §4.2',
    }),
    buildPayment({
      n: 2,
      amount: '6800.00',
      status: 'paid',
      method: 'ach',
      paymentDate: daysFromNow(-15),
      supplier: coastal,
      invoiceId: '33333333-0000-4000-8000-000000000099',
      invoiceNumber: 'AIG-89740',
      createdAt: daysFromNow(-18),
    }),
  ];

  // --- PO line-item lookup (keyed by PO line-item id). ---------------------
  const poLineItems: Record<string, GetPurchaseOrderLineItemResponse> = {};
  for (const li of poLineItemsArr) {
    poLineItems[li.id] = { lineItem: li, purchaseOrder };
  }
  for (const li of flPoLineItemsArr) {
    poLineItems[li.id] = { lineItem: li, purchaseOrder: flPurchaseOrder };
  }
  for (const li of vcPoLineItemsArr) {
    poLineItems[li.id] = { lineItem: li, purchaseOrder: vcPurchaseOrder };
  }

  return {
    invoices: [atlInvoice, freightInvoice, freshLinkInvoice, valleyColdInvoice],
    invoiceDetails: {
      [atlInvoice.id]: atlDetail,
      [freightInvoice.id]: freightDetail,
      [freshLinkInvoice.id]: {
        invoice: freshLinkInvoice,
        lineItems: buildLineItems(flLines, flLineId),
        inboundEmail: null,
        relatedConversations: [],
      },
      [valleyColdInvoice.id]: {
        invoice: valleyColdInvoice,
        lineItems: buildLineItems(vcLines, vcLineId),
        inboundEmail: null,
        relatedConversations: [],
      },
    },
    changeLogs: {
      [atlInvoice.id]: heroChangeLog,
      [freightInvoice.id]: frtChangeLog,
      [freshLinkInvoice.id]: flChangeLog,
      [valleyColdInvoice.id]: vcChangeLog,
    },
    supportingDocs: {
      [atlInvoice.id]: [podDoc],
    },
    emailThreads: {
      [AP_DEMO_IDS.conversationSource]: toEmailThread(
        sourceEmail,
        AP_DEMO_IDS.conversationSource,
      ),
      [AP_DEMO_IDS.conversationCarrier]: toEmailThread(
        carrierEmailMsg,
        AP_DEMO_IDS.conversationCarrier,
      ),
    },
    suppliers: {
      [coastal.id]: coastal,
      [freshLink.id]: freshLink,
      [valleyCold.id]: valleyCold,
      [coldChain.id]: coldChain,
    },
    purchaseOrders: {
      [AP_DEMO_IDS.po]: poResponse,
      [AP_DEMO_IDS.poFreshLink]: flPoResponse,
      [AP_DEMO_IDS.poValleyCold]: vcPoResponse,
    },
    poLineItems,
    shipments: {
      [AP_DEMO_IDS.shipment]: shipment,
    },
    charges: {
      [AP_DEMO_IDS.freightCharge]: freightCharge,
    },
    associations: {
      ...associations,
      ...flAssociations,
      ...vcAssociations,
      ...frtAssociations,
    },
    supplierProvenanceByInvoiceId: {
      [atlInvoice.id]: {
        kind: 'memory-note',
        title: 'Matched via memory-bank note',
        detail:
          '"Apex Industrial Group" is the parent billing entity for vendor "MRO Supply Co.". The invoice is billed under the parent name, so the vendor name never appears on it — we matched on the learned mapping.',
        actor: 'Jordan Ellis',
        when: dateOnly(daysFromNow(-112)),
      },
    },
    lineItemProvenanceById: {
      [heroLineId(1)]: {
        kind: 'web-search',
        title: 'Matched via web search',
        detail:
          'Invoice line "Unistrut Channel" did not match any PO line by name. A web agent confirmed "Unistrut" is a brand name used generically for metal framing strut channel, so it matched to PO line "Strut Channel (12 ga)".',
        query: 'is unistrut the same as strut channel',
        resultSnippet:
          '“Unistrut” is a registered trademark (Atkore) for metal framing channel; in the trade the name is used generically for strut channel from any maker.',
        sourceUrl: 'https://www.atkore.com/unistrut',
        sourceLabel: 'atkore.com',
      },
    },
    threeWayByInvoiceId: {
      [atlInvoice.id]: threeWay,
    },
    extractedDocsByDocumentId: {
      [AP_DEMO_IDS.podDocument]: podExtracted,
    },
    earlyPayApprovedByInvoiceId: {},
    lineMemorySavedById: {},
    payments,
    transactions: [],
    logisticsLinkByInvoiceId,
    remittance: null,
    supplierHistory: { open: false },
    apWorkflow: { built: false, confidenceAdded: false },
  };
}
